import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from '../../RootStore';
import { expenseService } from '../../../services/modules/expense/expense.service';
import { IExpenseResult } from '../../../models/expense/ExpenseModel';
import { toast } from 'react-toastify';
import { GridApi } from 'ag-grid-community';

interface ODataParams {
  $filter?: string;
  $orderby?: string;
  $skip?: number;
  $top?: number;
  $select?: string;
  $count?: boolean;
}

interface GridParams {
  startRow: number;
  endRow: number;
  filterModel: any;
  sortModel: any;
}

export class ExpenseStore {
  rootStore: RootStore;
  expenseList: IExpenseResult[] = [];
  selectedExpense: IExpenseResult | null = null;
  error: string | null = null;
  gridApi: GridApi<IExpenseResult> | null = null;
  selectedExpenseToDelete: number | null = null;
  selectedUserId: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setGridApi = (api: GridApi<IExpenseResult>) => {
    this.gridApi = api;
  };

  setSelectedExpense = (expense: IExpenseResult | null) => {
    this.selectedExpense = expense;
  };

  setSelectedExpenseToDelete = (id: number | null) => {
    this.selectedExpenseToDelete = id;
  };

  setSelectedUserId = (userId: string | null) => {
    this.selectedUserId = userId;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  reset = () => {
    this.selectedExpense = null;
    this.error = null;
    this.selectedExpenseToDelete = null;
    this.selectedUserId = null;
  };

  refreshGrid = () => {
    if (this.gridApi) {
      this.gridApi.refreshServerSide();
      this.gridApi.refreshInfiniteCache();
      this.gridApi.redrawRows({ rowNodes: [] });
    }
  };

  listExpenses = async (
    params: GridParams
  ): Promise<{ rows: IExpenseResult[]; lastRow: number; count: number }> => {
    try {
      const odataParams: ODataParams = {
        $count: true,
        $skip: params.startRow,
        $top: params.endRow - params.startRow,
      };

      const filters: string[] = [];

      // Admin seçili bir kullanıcı seçtiyse onun kayıtlarını göster
      if (this.selectedUserId) {
        filters.push(`userId eq '${this.selectedUserId}'`);
      }

      if (params.filterModel) {
        const filterModelFilters = Object.entries(params.filterModel).map(
          ([field, filter]: [string, any]) => {
            const numericFields = ['id', 'amount'];
            if (numericFields.includes(field)) {
              return `${field} eq ${filter.filter}`;
            }
            return `contains(${field}, '${filter.filter}')`;
          }
        );

        filters.push(...filterModelFilters);
      }

      if (filters.length > 0) {
        odataParams.$filter = filters.join(' and ');
      }

      if (params.sortModel && params.sortModel.length > 0) {
        const { colId, sort } = params.sortModel[0];
        odataParams.$orderby = `${colId} ${sort === 'asc' ? 'asc' : 'desc'}`;
      }

      const response: any = await expenseService.listExpenses({
        params: odataParams,
      });

      return runInAction(() => {
        if (response) {
          const totalCount = response['@odata.count'] || 0;
          return {
            rows: response.value || [],
            lastRow: totalCount,
            count: totalCount,
          };
        }
        return {
          rows: [],
          lastRow: 0,
          count: 0,
        };
      });
    } catch (error) {
      console.error('Error fetching expenses:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
      throw error;
    }
  };

  createExpense = async (formData: FormData) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await expenseService.createExpense(formData);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        toast.error(response.message || 'Harcama kaydı oluşturulamadı');
        return response;
      }

      runInAction(() => {
        this.error = null;
        this.refreshGrid();
      });

      toast.success('Harcama kaydı başarıyla oluşturuldu');
      return response;
    } catch (error) {
      console.error('Error creating expense:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
        toast.error('Harcama kaydı oluşturulurken bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  updateExpense = async (id: number, formData: FormData) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await expenseService.updateExpense(id, formData);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        toast.error(response.message || 'Harcama kaydı güncellenemedi');
        return response;
      }

      runInAction(() => {
        this.error = null;
        this.refreshGrid();
      });

      toast.success('Harcama kaydı başarıyla güncellendi');
      return response;
    } catch (error) {
      console.error('Error updating expense:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
        toast.error('Harcama kaydı güncellenirken bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  deleteExpense = async (id: number) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await expenseService.deleteExpense(id);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        toast.error(response.message || 'Harcama kaydı silinemedi');
        return response;
      }

      runInAction(() => {
        this.error = null;
        this.refreshGrid();
      });

      toast.success('Harcama kaydı başarıyla silindi');
      return response;
    } catch (error) {
      console.error('Error deleting expense:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
        toast.error('Harcama kaydı silinirken bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  getExpenseById = async (id: number): Promise<IExpenseResult | null> => {
    try {
      const response: any = await expenseService.getExpenseById(id);
      if (response.error || !response.value) {
        return null;
      }
      return response.value;
    } catch (error) {
      console.error('Error fetching expense by id:', error);
      return null;
    }
  };
}
